/**
 * Overtime API Service
 */

import { apiClient } from '../client';
import type {
  ApiResponse,
  PaginatedData,
  OvertimeInitData,
  OvertimeType,
  OvertimeApplication,
  OvertimeApplicationDetail,
  OvertimeApplyRequest,
  OvertimeCalculateRequest,
  OvertimeCalculateResult,
  OvertimeDetectTypeResult,
} from '../../types';

const API_PREFIX = '/api/saiens/overtime';

/**
 * Get overtime init data (types + time options)
 */
export async function getOvertimeInit(): Promise<ApiResponse<OvertimeInitData>> {
  const response = await apiClient.get<ApiResponse<OvertimeInitData>>(
    `${API_PREFIX}/init`
  );
  return response.data;
}

/**
 * Get overtime types
 */
export async function getOvertimeTypes(): Promise<ApiResponse<OvertimeType[]>> {
  const response = await apiClient.get<ApiResponse<OvertimeType[]>>(
    `${API_PREFIX}/types`
  );
  return response.data;
}

/**
 * Detect overtime type based on date
 */
export async function detectOvertimeType(
  date: string
): Promise<ApiResponse<OvertimeDetectTypeResult>> {
  const response = await apiClient.get<ApiResponse<OvertimeDetectTypeResult>>(
    `${API_PREFIX}/detect-type`,
    { params: { date } }
  );
  return response.data;
}

/**
 * Calculate overtime hours
 */
export async function calculateOvertimeHours(
  data: OvertimeCalculateRequest
): Promise<ApiResponse<OvertimeCalculateResult>> {
  const response = await apiClient.post<ApiResponse<OvertimeCalculateResult>>(
    `${API_PREFIX}/calculate-hours`,
    data
  );
  return response.data;
}

interface OvertimeListParams {
  offset?: number;
  limit?: number;
  state?: string;
}

/**
 * Get overtime applications list
 */
export async function getOvertimeApplications(
  params: OvertimeListParams = {}
): Promise<ApiResponse<PaginatedData<OvertimeApplication>>> {
  const response = await apiClient.get<ApiResponse<PaginatedData<OvertimeApplication>>>(
    `${API_PREFIX}/applications`,
    { params }
  );
  return response.data;
}

/**
 * Get overtime application detail
 */
export async function getOvertimeApplication(
  id: number
): Promise<ApiResponse<OvertimeApplicationDetail>> {
  const response = await apiClient.get<ApiResponse<OvertimeApplicationDetail>>(
    `${API_PREFIX}/applications/${id}`
  );
  return response.data;
}

/**
 * Create overtime application (draft)
 */
export async function createOvertimeApplication(
  data: OvertimeApplyRequest
): Promise<ApiResponse<OvertimeApplicationDetail>> {
  const response = await apiClient.post<ApiResponse<OvertimeApplicationDetail>>(
    `${API_PREFIX}/applications`,
    data
  );
  return response.data;
}

/**
 * Update overtime application
 */
export async function updateOvertimeApplication(
  id: number,
  data: Partial<OvertimeApplyRequest>
): Promise<ApiResponse<OvertimeApplicationDetail>> {
  const response = await apiClient.put<ApiResponse<OvertimeApplicationDetail>>(
    `${API_PREFIX}/applications/${id}`,
    data
  );
  return response.data;
}

/**
 * Delete overtime application (draft only)
 */
export async function deleteOvertimeApplication(
  id: number
): Promise<ApiResponse<void>> {
  const response = await apiClient.delete<ApiResponse<void>>(
    `${API_PREFIX}/applications/${id}`
  );
  return response.data;
}

/**
 * Submit overtime application (draft -> f_approve)
 */
export async function submitOvertimeApplication(
  id: number
): Promise<ApiResponse<OvertimeApplicationDetail>> {
  const response = await apiClient.post<ApiResponse<OvertimeApplicationDetail>>(
    `${API_PREFIX}/applications/${id}/submit`
  );
  return response.data;
}

/**
 * Cancel overtime application (f_approve -> draft)
 */
export async function cancelOvertimeApplication(
  id: number
): Promise<ApiResponse<OvertimeApplicationDetail>> {
  const response = await apiClient.post<ApiResponse<OvertimeApplicationDetail>>(
    `${API_PREFIX}/applications/${id}/cancel`
  );
  return response.data;
}
